<template>
  <Layout
    class="onboarding-books"
    :title="page.title"
    :subtitle="page.subtitle"
  >
    <div class="onboarding-books__cover-section">
      <Transition name="fade" mode="out-in">
        <div :key="currentBook" class="onboarding-books__cover-wrapper">
          <img
            :src="currentBookCover"
            :alt="currentBook"
            width="223"
            height="298"
            class="onboarding-books__cover"
          />
          <img
            :src="currentBookCover"
            :alt="currentBook"
            class="onboarding-books__cover onboarding-books__cover--background"
          />
        </div>
      </Transition>
    </div>

    <div class="onboarding-books__controls-wrapper">
      <div class="onboarding-books__controls">
        <button
          class="onboarding-books__button onboarding-books__button--negative"
          @click.prevent="nextBook(-1)"
        >
          <span class="iconfont idislike"></span>
        </button>
        <button
          class="onboarding-books__button onboarding-books__button--neutral"
          @click.prevent="nextBook(0)"
        >
          <img src="@/assets.app/images/onboarding/shrug-emoji.png" alt="" />
        </button>
        <button
          class="onboarding-books__button onboarding-books__button--positive"
          @click.prevent="nextBook(1)"
        >
          <span class="iconfont ilike"></span>
        </button>
      </div>

      <div class="onboarding-books__controls-labels">
        <p class="onboarding-books__controls-label">Not at all</p>
        <p class="onboarding-books__controls-label">Very interested</p>
      </div>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

import { BooksChoice, BooksPage } from '@/models/onboarding.types'
import { getImage, QuestionnaireView } from '@/models/onboarding'

import Layout from './Layout.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as BooksPage
})

const currentChoices = computed(() => {
  return props.view.currentChoice as BooksChoice
})

const emit = defineEmits<{
  (e: 'done'): void
}>()

const currentBook = computed(() => {
  return page.value.content[props.view.currentSubpageIndex]
})

const currentBookCover = computed(() => {
  return getImage(`${currentBook.value}.png`)
})

function nextBook(score: number): void {
  props.view.setCurrentChoice([
    ...currentChoices.value,
    { id: currentBook.value, score },
  ])

  const nextBookExists = props.view.nextSubpage()

  if (!nextBookExists) {
    emit('done')
  }
}

onMounted(() => {
  props.view.setCurrentChoice([])
})
</script>

<style lang="scss" scoped>
.onboarding-books {
  padding-bottom: var(--onboarding-padding);

  :deep(.onboarding-layout__text) {
    margin-bottom: 18px;
  }

  :deep(.onboarding-layout__default-slot) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    gap: 8px;
    overflow-y: hidden;
    @media only screen and (min-height: 813px) {
      justify-content: flex-start;
      gap: 30px;
    }
  }

  &__cover-section {
    min-height: 0;
  }

  &__cover-wrapper {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 36px;
    border-radius: 14px;
    overflow: hidden;
    @media only screen and (max-height: 710px) {
      padding: 16px;
    }
    @media (min-width: $w-medium-b) {
      max-width: 327px;
      max-height: 368px;
      margin-inline: auto;
    }

    @media (min-width: $w-pc-b) {
      max-width: 271px;
      max-height: 306px;
    }
  }

  &__cover {
    width: auto;
    max-width: 223px;
    max-height: 100%;
    filter: drop-shadow(0px 1px 5px rgba(255, 255, 255, 0.1))
      drop-shadow(0px 8px 15px rgba(20, 28, 49, 0.1));
    z-index: 1;
    @media only screen and (min-height: 813px) {
      width: 223px;
    }
    @media (min-width: $w-pc-b) {
      max-width: 183px;
    }
  }

  &__cover--background {
    position: absolute;
    pointer-events: none;
    position: absolute;
    z-index: 0;
    inset: 0;
    transform: scale(5);
    filter: blur(30px) saturate(1.3);
    opacity: 0.3;
  }

  &__controls-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  &__controls-labels {
    display: flex;
    width: 100%;
    justify-content: space-between;
    margin-top: 8px;
  }

  &__controls-label {
    font-size: 14px;
    line-height: 24px;
    letter-spacing: -0.02em;
    color: $c-black-6;
    margin: 0;
  }

  &__controls {
    position: relative;
    display: flex;
    width: 100%;
    background: $c-white;
    padding-block: 16px;
    border: 1px solid $c-border-2;
    border-radius: 14px;
    @media only screen and (max-height: 500px) {
      padding-block: 12px;
    }
  }

  &__button {
    --icon-size: 40px;
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    border-inline: 1px solid transparent;

    img {
      width: var(--icon-size);
    }

    .iconfont {
      width: var(--icon-size);
      height: var(--icon-size);
      font-size: var(--icon-size);
    }

    &:not(:last-child) {
      border-right: 1px solid $c-border-2;
    }
  }

  &__button--positive {
    color: #7ed86f;
  }

  &__button--negative {
    color: #ea726d;
  }
}
</style>
